import React from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import c2pCentralLoginLink from './c2pCentralLink';

/* The body of an announcement, rendered as markdown.
 *
 * One component for all four places an announcement body appears - the employee
 * dashboard, the HR editor, the Entrepreneur (B2B) staff editor and the firm
 * member dashboard - so a link behaves the same wherever it is read. Before
 * this, only the two HR surfaces rendered markdown at all; the B2B pair printed
 * the raw text while their editor promised markdown worked.
 *
 * remark-gfm is here for autolink literals: staff paste a bare URL far more
 * often than they type [text](url), and a pasted URL that stays plain text is
 * exactly the complaint this component answers. remark-breaks keeps a single
 * Enter in the textarea as a line break - plain markdown folds it into the
 * paragraph, which would have silently reflowed every B2B announcement already
 * posted (that card used to print the text as typed).
 *
 * C2P Central links get routed through that site's login page on the way out -
 * see c2pCentralLink.js for why. The reader still sees whatever the author
 * wrote as the link text; only the href changes.
 *
 * Safety: react-markdown does not parse raw HTML unless rehype-raw is added
 * (it is not), and its default urlTransform drops anything that is not http,
 * https, mailto, tel or a relative path - so a javascript: href cannot get
 * through even though B2B announcements are read outside the firm.
 */

// Hub teal - readable on both the white cards and the cream canvas.
const DEFAULT_LINK_COLOR = '#0069a6';

export default function AnnouncementBody({ children = '', style, linkColor = DEFAULT_LINK_COLOR }) {
  const components = React.useMemo(() => ({
    a: ({ href, children: label }) => {
      const target = c2pCentralLoginLink(href);
      // Only a web link earns a new tab. A mailto: or tel: handled in a new tab
      // leaves the reader with a blank window, and a relative hub path should
      // stay in the app.
      const newTab = /^https?:\/\//i.test(target || '');
      return (
        <a
          href={target}
          target={newTab ? '_blank' : undefined}
          rel={newTab ? 'noopener noreferrer' : undefined}
          style={{ color: linkColor, fontWeight: 600, textDecoration: 'underline' }}
        >
          {label}
        </a>
      );
    },
  }), [linkColor]);

  return (
    <div className="announcement-body" style={style}>
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={components}>
        {children || ''}
      </ReactMarkdown>
    </div>
  );
}

AnnouncementBody.propTypes = {
  children: PropTypes.string,
  style: PropTypes.object,
  linkColor: PropTypes.string,
};
